import * as Sentry from "@sentry/react";
import { browserTracingIntegration, replayIntegration } from "@sentry/react";
import { Component, type ReactNode, type ErrorInfo } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { initWebVitals, initUnhandledErrorReporting, reportError } from "./utils/performance";
import { initOtel } from "./utils/otel";
import { initPwaGuard } from "./lib/pwa-guard";
import { initRumBeacon, recordFirstReactRender, recordSplashDismissed } from "./utils/rum-beacon";

try { initPwaGuard(); } catch (_) {}

// GAP-035 — Suppress the `data-replit-metadata` prop warning injected by the
// Replit IDE into Fragment root elements. This warning is a false-positive:
// the prop originates from the dev-tooling overlay, not from application code.
// We filter it only in development so it never reaches production bundles.
if (import.meta.env.DEV) {
  const _origConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    const first = args[0];
    if (typeof first === "string" && first.includes("data-replit-metadata")) return;
    _origConsoleError(...args);
  };
}

// Browser OTel tracing is gated behind PLATFORM_TELEMETRY_OPT_IN.
// When the flag is absent or false (the default), the SDK never initialises
// and the browser sends zero requests to /api/telemetry/otlp/v1/traces.
// Sentry error capture (below) remains active unconditionally.
if (import.meta.env.VITE_PLATFORM_TELEMETRY_OPT_IN === "true") {
  try { initOtel(); } catch (_) {}
}
try { initWebVitals(); } catch (_) {}
try { initUnhandledErrorReporting(); } catch (_) {}
// Public RUM beacon: must run synchronously at module load to read
// PerformanceNavigationTiming before the next repaint may truncate it.
try { initRumBeacon(); } catch (_) {}

// Task #3392 — Defer Sentry init via requestIdleCallback / setTimeout(0) so
// the SDK's replay + tracing integrations do not block the critical-path
// module evaluation that renders the login page. The pre-loader dismissal in
// RootErrorBoundary means the app is already visible; Sentry wiring can wait.
if (import.meta.env.VITE_SENTRY_DSN) {
  const _initSentry = () => {
    try {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN as string,
        environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) || import.meta.env.MODE,
        release: (import.meta.env.VITE_SENTRY_RELEASE as string) || undefined,
        integrations: [
          browserTracingIntegration(),
          // Task #1959 — Observability gold: Sentry Browser session replay.
          // Aggressive PII masking: every text node, every input value, and
          // every media element is masked/blocked. Only structural DOM is
          // captured so a replay never leaks worker PII or partner data.
          replayIntegration({
            maskAllText: true,
            maskAllInputs: true,
            blockAllMedia: true,
          }),
        ],
        tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
        // Replay sampling — keep 10% of normal sessions and 100% of error sessions.
        replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 0,
        replaysOnErrorSampleRate: 1.0,
        tracePropagationTargets: ["localhost", /^\/api\//],
      });
    } catch (_) {}
  };
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    (window as any).requestIdleCallback(_initSentry, { timeout: 3000 });
  } else {
    setTimeout(_initSentry, 0);
  }
}

// ─── Pre-loader dismissal ─────────────────────────────────────────────────────
//
// Four-layer strategy so the HTML splash screen (#pre) is ALWAYS dismissed
// regardless of how React behaves in production:
//
//   L1  RootErrorBoundary.componentDidMount  — normal case: React mounts OK
//   L2  RootErrorBoundary.componentDidCatch  — React crashes: show error UI
//   L3  setTimeout(12 000) from bundle load  — React hangs/stalls before mount
//   L4  HTML inline <script> 25 s from page  — JS bundle never executed at all
//       (added in index.html; uses the same pre.__done flag for coordination)
//
// Task #3408 — The dismissal is now coordinated with auth resolution:
//   - L1 registers the dismissal function at window.__nashidAuthReady so
//     AuthProvider can call it at the exact moment isLoading → false.
//   - If auth already resolved before React mounted (unlikely but safe),
//     the flag is "done" and dismissHTMLPreloader is called immediately.
//   - L2 still calls dismissHTMLPreloader directly (crash path — immediate).
//   - L3/L4 safety nets remain unchanged.
//
// The `pre.__done` flag prevents any layer from double-firing.
function dismissHTMLPreloader(): void {
  const pre = document.getElementById("pre") as HTMLElement | null;
  if (!pre || (pre as any).__done) return;
  (pre as any).__done = true;
  // RUM milestone: record splash-dismissed timing then flush the beacon.
  try { recordSplashDismissed(); } catch { /* ignore */ }
  pre.classList.add("out");
  setTimeout(() => { try { pre.remove(); } catch { /* ignore */ } }, 300);
}

// L3 — fallback fired 12 s after bundle load in case React hangs before mount.
// GAP-034 — If the root element still has no children when L3 fires, React has
// stalled entirely. Replace the blank root with a vanilla-DOM "Something went
// wrong" message so the user is never left looking at a white screen.
setTimeout(() => {
  const root = document.getElementById("root");
  if (root && !root.hasChildNodes()) {
    root.innerHTML =
      '<div style="min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;background:#f8fafc;padding:24px;font-family:system-ui,sans-serif">' +
        '<div style="text-align:center;max-width:400px">' +
          '<div style="width:48px;height:48px;border-radius:50%;background:#fff7ed;display:flex;align-items:center;justify-content:center;margin:0 auto 16px;border:2px solid #f97316">' +
            '<span style="color:#f97316;font-size:20px;font-weight:bold" aria-hidden="true">!</span>' +
          '</div>' +
          '<h2 style="font-size:18px;font-weight:600;color:#0f172a;margin:0 0 8px">Something went wrong &mdash; please reload</h2>' +
          '<p style="font-size:14px;color:#64748b;margin:0 0 16px">The application took too long to start. This may be a temporary issue.</p>' +
          '<button onclick="window.location.reload()" style="padding:8px 20px;background:#f97316;color:white;border:none;border-radius:6px;font-size:14px;font-weight:500;cursor:pointer">Reload</button>' +
        '</div>' +
      '</div>';
  }
  dismissHTMLPreloader();
}, 12_000);

// ─── Error fallback UI ────────────────────────────────────────────────────────
const ErrorFallbackUI = () => (
  <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#f8fafc", padding: "24px" }}>
    <div style={{ textAlign: "center", maxWidth: "400px" }}>
      <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "#fff7ed", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", border: "2px solid #f97316" }}>
        <span style={{ color: "#f97316", fontSize: "20px", fontWeight: "bold" }}>!</span>
      </div>
      <h2 style={{ fontSize: "18px", fontWeight: "600", color: "#0f172a", marginBottom: "8px" }}>Application Error</h2>
      <p style={{ fontSize: "14px", color: "#64748b", marginBottom: "16px" }}>
        The application failed to load. Please reload the page.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{ padding: "8px 20px", background: "#f97316", color: "white", border: "none", borderRadius: "6px", fontSize: "14px", fontWeight: "500", cursor: "pointer" }}
      >
        Reload
      </button>
    </div>
  </div>
);

// ─── Root error boundary ──────────────────────────────────────────────────────
// Wraps the entire tree. Hosts L1 (componentDidMount) and L2 (componentDidCatch)
// so the HTML pre-loader is dismissed the moment React either successfully
// commits its first render or catches a render-phase error.
class RootErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  // L1 — React mounted successfully.
  // Task #3408: instead of calling dismissHTMLPreloader() directly here, we
  // register it at window.__nashidAuthReady so AuthProvider can trigger it the
  // exact moment the auth check resolves (isLoading: true → false).
  // Edge case: if auth resolved before React mounted (e.g. instant cache hit),
  // the flag will already be "done" — dismiss immediately in that case.
  componentDidMount() {
    // RUM milestone: React has committed its first paint.
    try { recordFirstReactRender(); } catch { /* ignore */ }
    if ((window as any).__nashidAuthReady === "done") {
      // Auth already resolved — dismiss immediately.
      dismissHTMLPreloader();
    } else {
      // Register the dismissal for AuthProvider to call.
      (window as any).__nashidAuthReady = dismissHTMLPreloader;
    }
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  // L2 — React render crashed: dismiss pre-loader so the error UI is visible.
  componentDidCatch(error: Error, info: ErrorInfo) {
    dismissHTMLPreloader();
    console.error("[Root] Uncaught render error:", error.message, info.componentStack);
    reportError({
      message: error.message,
      stack: error.stack,
      page: typeof window !== "undefined" ? window.location.pathname : "/",
      componentStack: info.componentStack ?? undefined,
    });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallbackUI />;
    }
    return this.props.children;
  }
}

const sentryFallback = <ErrorFallbackUI />;

createRoot(document.getElementById("root")!).render(
  <RootErrorBoundary>
    <Sentry.ErrorBoundary fallback={sentryFallback}>
      <App />
    </Sentry.ErrorBoundary>
  </RootErrorBoundary>
);
